import React, { useEffect, useState } from 'react'

import { Dialog3 as Dialog } from '@st-operations/uikit/src/v1/index.js'
import {
  Button,
  BUTTON_THEME,
  IconButton,
  Input,
  Select,
} from '@st-operations/uikit/src/v2/index.js'

import { useDestinations, useRegions } from '_hooks'

import './styles.scss'

const _unique = (arr, key) => {
  const keys = new Set()
  return arr.filter((el) => !keys.has(el[key]) && keys.add(el[key]))
}

const unique = (type, obj) => {
  const all = Object.values(obj)
    .flatMap((item) => item[type])
    .filter(Boolean)
  return _unique(all, '_id')
}

const ModalMergeAudience = ({ selectedAudiences, isModalMergeOpen, merge, closeModal }) => {
  const actionLabel = 'Merge Audiences'
  const [audiences, setAudiences] = useState({})
  const [name, setName] = useState('')
  const [uniqueRegions, setUniqueRegions] = useState([])
  const [uniqueDestinations, setUniqueDestinations] = useState([])
  const regions = useRegions()
  const destinations = useDestinations()

  useEffect(() => {
    setUniqueRegions(unique('regions', audiences))
    setUniqueDestinations(unique('destinations', audiences))
    setName(
      Object.values(audiences)
        .map((audience) => audience.name.split('>').at(-1).trim())
        .join(' + ')
    )
  }, [audiences])

  useEffect(() => {
    setAudiences(
      selectedAudiences.reduce(
        (acc, audience) => ({
          ...acc,
          [audience._id]: audience,
        }),
        {}
      )
    )
  }, [selectedAudiences])

  useEffect(() => {
    if (isModalMergeOpen && selectedAudiences.length === 0) {
      closeModal()
      toast.error('No audience selected.')
    }
    if (isModalMergeOpen && selectedAudiences.length === 1) {
      closeModal()
      toast.error('You need at least two audiences.')
    }
  }, [isModalMergeOpen && selectedAudiences])

  const handleChange = (value, name) => {
    switch (name) {
      case 'regions':
        setUniqueRegions(value)
        break
      case 'destinations':
        setUniqueDestinations(value)
        break
    }
  }

  const handleChangeName = (newName) => setName(newName)

  return (
    <Dialog open={isModalMergeOpen} className="modal-clone-audience">
      <header className="modal-clone-audience__header">
        <h2>{actionLabel}</h2>
        <IconButton
          onClick={closeModal}
          aria-label="Close modal"
          iconProps={{
            icon: 'delete',
            type: 'fill',
            width: 16,
            height: 16,
            color: '#3d3d3d',
          }}
        />
      </header>
      <section className="modal-clone-audience__audiences-list">
        <div className="modal-clone-audience__audiences-item">
          <h4 className="modal-clone-audience__name">{name}</h4>
          <Input name="name" label="Audience Name" value={name} onChange={handleChangeName} />
          <Select
            name="regions"
            label="Regions"
            className="audience-creator__regions-field"
            isMulti
            isSearchable
            useReactSelect
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            options={regions?.filter(
              (region) =>
                !uniqueRegions?.some((audienceRegion) => audienceRegion.name === region.name)
            )}
            value={uniqueRegions?.sort((a, b) => (a.name > b.name ? 1 : -1))}
            onChange={(newRegions) => {
              handleChange(newRegions || [], 'regions')
            }}
          />
          <Select
            name="destinations"
            label="Destinations"
            isMulti
            isSearchable
            useReactSelect
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            options={destinations.filter(
              (destination) =>
                !uniqueDestinations?.some(
                  (audienceDestination) => audienceDestination.name === destination.name
                )
            )}
            value={uniqueDestinations
              ?.sort((a, b) => (a.name > b.name ? 1 : -1))
              .map((destination) => ({
                ...destination,
                label: destination.name,
              }))}
            onChange={(destinations) => {
              handleChange(destinations || [], 'destinations')
            }}
          />
        </div>
      </section>
      <footer className="modal-clone-audience__footer">
        <div className="modal-clone-audience__actions">
          <Button onClick={closeModal} theme={BUTTON_THEME.SECONDARY}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              merge({
                ids: Object.keys(audiences),
                name,
                regions: uniqueRegions,
                destinations: uniqueDestinations,
              })
              closeModal()
            }}
          >
            {actionLabel}
          </Button>
        </div>
      </footer>
    </Dialog>
  )
}

export default ModalMergeAudience
